import { useEffect, useCallback, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import {
  createZodResolver,
  createMaturityAssessmentSchema,
  type CreateMaturityAssessmentFormData,
  type AssessmentResponseFormData,
} from '../forms/validation';
import type { MaturityFramework } from '../../graphql/types';
import { CapabilityCriteriaChecklist } from './CapabilityCriteriaChecklist';
import { CapabilityLevelDescriptions } from './CapabilityLevelDescriptions';
import '@tylertech/forge/button';
import '@tylertech/forge/card';
import './AssessmentForm.css';

export interface AssessmentFormProps {
  framework: MaturityFramework;
  defaultValues?: Partial<CreateMaturityAssessmentFormData>;
  onSubmit: (data: CreateMaturityAssessmentFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  mode: 'create' | 'edit';
  serviceId: string;
}

const LEVEL_LABELS: Record<number, string> = {
  1: 'Initial',
  2: 'Basic',
  3: 'Repeatable',
  4: 'Managed',
  5: 'Mature',
  6: 'Advanced',
};

function buildDefaultResponses(framework: MaturityFramework): AssessmentResponseFormData[] {
  return framework.areas.flatMap((area) =>
    area.categories.flatMap((cat) =>
      cat.capabilities.map((cap) => ({
        capabilityId: cap.id,
        level: 3,
        comment: undefined,
      }))
    )
  );
}

function computeOverallScore(levels: number[]): number {
  if (levels.length === 0) return 0;
  return levels.reduce((sum, l) => sum + l, 0) / levels.length;
}

/**
 * Determine whether a capability has any unmet criteria for the selected level.
 * A capability has "unmet criteria" when the assessor has flagged one or more
 * of the level checklist items as not yet confirmed.
 */
function hasUnmetCriteria(unmetLevelIds: Set<number>): boolean {
  return unmetLevelIds.size > 0;
}

export function AssessmentForm({
  framework,
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
  serviceId,
}: AssessmentFormProps): JSX.Element {
  const CardElement = 'forge-card' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm<CreateMaturityAssessmentFormData>({
    resolver: createZodResolver(createMaturityAssessmentSchema),
    defaultValues: {
      frameworkId: framework.entityId,
      subjectEntityType: 'Service',
      subjectEntityId: serviceId,
      assessmentPeriod: '',
      dueDate: '',
      responses: buildDefaultResponses(framework),
      ...defaultValues,
    },
  });

  const { fields } = useFieldArray({ control, name: 'responses' });
  const responsesWatch = watch('responses') ?? [];
  const overallScore = computeOverallScore(responsesWatch.map((r) => r?.level ?? 3));

  /**
   * Per-capability unmet-criteria state.
   * Key: capabilityId, Value: Set of level numbers marked as not yet met.
   */
  const [unmetCriteriaMap, setUnmetCriteriaMap] = useState<Map<string, Set<number>>>(
    () => new Map()
  );

  /**
   * Tracks which capability IDs have been explicitly confirmed by the assessor
   * (i.e., all unmet flags toggled back to met). The checklist collapses for
   * confirmed capabilities. Value = level number at which confirmation occurred
   * so the checklist expands again if the level changes.
   */
  const [confirmedCapabilities, setConfirmedCapabilities] = useState<Map<string, number>>(
    () => new Map()
  );

  const toggleUnmetLevel = useCallback(
    (capabilityId: string, levelNumber: number, currentLevel: number): void => {
      setUnmetCriteriaMap((prev) => {
        const next = new Map(prev);
        const existing = next.get(capabilityId) ?? new Set<number>();
        const updated = new Set(existing);
        if (updated.has(levelNumber)) {
          updated.delete(levelNumber);
          // If this was the last unmet item being cleared, mark as confirmed
          if (updated.size === 0) {
            setConfirmedCapabilities((prevConf) => {
              const nextConf = new Map(prevConf);
              nextConf.set(capabilityId, currentLevel);
              return nextConf;
            });
          }
        } else {
          updated.add(levelNumber);
          // Un-confirmed when an item is flagged as unmet
          setConfirmedCapabilities((prevConf) => {
            const nextConf = new Map(prevConf);
            nextConf.delete(capabilityId);
            return nextConf;
          });
        }
        next.set(capabilityId, updated);
        return next;
      });
    },
    []
  );

  // Clear unmet criteria AND confirmed state when level changes.
  useEffect(() => {
    setUnmetCriteriaMap((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [capId, unmetSet] of next.entries()) {
        const capResponse = responsesWatch.find((r) => r?.capabilityId === capId);
        const selectedLevel = capResponse?.level ?? 3;
        // Remove any unmet flags for levels beyond the selected level
        const filtered = new Set([...unmetSet].filter((lvl) => lvl <= selectedLevel));
        if (filtered.size !== unmetSet.size) {
          next.set(capId, filtered);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // Clear confirmed state when the level changes
    setConfirmedCapabilities((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [capId, confirmedLevel] of next.entries()) {
        const capResponse = responsesWatch.find((r) => r?.capabilityId === capId);
        if (capResponse?.level !== confirmedLevel) {
          next.delete(capId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [responsesWatch]);

  useEffect(() => {
    if (defaultValues) {
      reset({
        frameworkId: framework.entityId,
        subjectEntityType: 'Service',
        subjectEntityId: serviceId,
        responses: buildDefaultResponses(framework),
        ...defaultValues,
      });
    }
  }, [defaultValues, framework, reset, serviceId]);

  return (
    <form className="assessment-form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <CardElement className="assessment-form__card">
        <div className="assessment-form__meta">
          <h3 className="forge-typography--subtitle1">Assessment Details</h3>

          <div className="assessment-form__meta-row">
            <div className="assessment-form__field">
              <label htmlFor="assessmentPeriod">Assessment Period *</label>
              <input
                id="assessmentPeriod"
                type="text"
                placeholder="e.g. 2026-Q1"
                {...register('assessmentPeriod')}
              />
              {errors.assessmentPeriod && (
                <span className="assessment-form__error">{errors.assessmentPeriod.message}</span>
              )}
            </div>

            <div className="assessment-form__field">
              <label htmlFor="dueDate">Due Date *</label>
              <input id="dueDate" type="date" {...register('dueDate')} />
              {errors.dueDate && (
                <span className="assessment-form__error">{errors.dueDate.message}</span>
              )}
            </div>
          </div>

          <div className="assessment-form__score-preview">
            <span className="forge-typography--body2">Overall Score Preview:</span>
            <strong>{overallScore.toFixed(2)} / 6</strong>
          </div>
        </div>
      </CardElement>

      {framework.areas.map((area) =>
        area.categories.map((cat) => (
          <CardElement key={cat.id} className="assessment-form__card">
            <div className="assessment-form__category">
              <h4 className="forge-typography--subtitle2">
                {area.name} — {cat.name}
              </h4>

              {cat.capabilities.map((cap) => {
                const fieldIndex = fields.findIndex((f) => f.capabilityId === cap.id);
                if (fieldIndex === -1) return null;

                // fieldIndex is a non-negative numeric result from findIndex (checked above) — not user-controlled
                // eslint-disable-next-line security/detect-object-injection
                const levelValue = responsesWatch[fieldIndex]?.level ?? 3;
                const unmetLevelIds = unmetCriteriaMap.get(cap.id) ?? new Set<number>();
                const hasUnmet = hasUnmetCriteria(unmetLevelIds);

                // Comment required for level 1/2 OR when any criteria are flagged as unmet
                const needsComment = levelValue <= 2 || hasUnmet;

                // Collapse checklist when all criteria are confirmed at the current level.
                // "Confirmed" means: no unmet flags AND the assessor explicitly cleared all
                // unmet flags (confirmedCapabilities tracks the level at which this occurred).
                const confirmedLevel = confirmedCapabilities.get(cap.id);
                const checklistCollapsed =
                  !hasUnmet && confirmedLevel === levelValue && levelValue >= 3;

                // Only show checklist if capability has levels with criteria or descriptions
                const hasLevels = cap.levels.length > 0;

                return (
                  <div key={cap.id} className="assessment-form__capability">
                    <div className="assessment-form__capability-header">
                      <span className="assessment-form__capability-name">{cap.name}</span>
                      <span className="assessment-form__level-label">
                        {/* levelValue is 1-6 from watch result clamped to valid range — not user-controlled */}
                        {/* eslint-disable-next-line security/detect-object-injection */}
                        {LEVEL_LABELS[levelValue] ?? ''}
                      </span>
                    </div>

                    <div className="assessment-form__level-selector">
                      {[1, 2, 3, 4, 5, 6].map((lvl) => (
                        <label key={lvl} className="assessment-form__level-option">
                          <Controller
                            control={control}
                            name={`responses.${fieldIndex}.level`}
                            render={({ field }) => (
                              <input
                                type="radio"
                                value={lvl}
                                checked={field.value === lvl}
                                onChange={() => field.onChange(lvl)}
                                // lvl is from hardcoded [1,2,3,4,5,6] array — not user-controlled
                                // eslint-disable-next-line security/detect-object-injection
                                aria-label={`Level ${lvl}: ${LEVEL_LABELS[lvl]}`}
                              />
                            )}
                          />
                          <span>{lvl}</span>
                        </label>
                      ))}
                    </div>

                    {hasLevels && (
                      <CapabilityLevelDescriptions levels={cap.levels} selectedLevel={levelValue} />
                    )}

                    {hasLevels && (
                      <CapabilityCriteriaChecklist
                        levels={cap.levels}
                        selectedLevel={levelValue}
                        unmetLevelIds={unmetLevelIds}
                        onToggleUnmet={(levelNumber) =>
                          toggleUnmetLevel(cap.id, levelNumber, levelValue)
                        }
                        collapsed={checklistCollapsed}
                      />
                    )}

                    {needsComment && (
                      <div className="assessment-form__comment">
                        <label htmlFor={`comment-${cap.id}`}>
                          Comment <span className="assessment-form__required">*</span>
                          <span className="assessment-form__hint">
                            {levelValue <= 2
                              ? 'Required for levels 1 and 2'
                              : 'Required when criteria are not yet met'}
                          </span>
                        </label>
                        <textarea
                          id={`comment-${cap.id}`}
                          rows={2}
                          placeholder="Describe the current state and improvement plan..."
                          {...register(`responses.${fieldIndex}.comment`)}
                        />
                        {/* fieldIndex is a non-negative numeric index — not user-controlled */}
                        {/* eslint-disable-next-line security/detect-object-injection */}
                        {errors.responses?.[fieldIndex]?.comment && (
                          <span className="assessment-form__error">
                            {/* eslint-disable-next-line security/detect-object-injection */}
                            {errors.responses[fieldIndex]?.comment?.message}
                          </span>
                        )}
                      </div>
                    )}

                    {hasUnmet && (
                      <div
                        className="assessment-form__unmet-warning"
                        role="alert"
                        aria-live="polite"
                      >
                        <span>
                          One or more criteria are not yet met — a comment is required to explain
                          the divergence.
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardElement>
        ))
      )}

      <div className="assessment-form__actions">
        <ForgeButton type="button" variant="outlined" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </ForgeButton>
        <ForgeButton type="submit" variant="raised" disabled={isSubmitting}>
          {mode === 'create' ? 'Create Assessment' : 'Update Assessment'}
        </ForgeButton>
      </div>
    </form>
  );
}
